import {
	type PageSearchOutput,
	type PageSearchPage,
	type ParsedPluginOutput,
	type PeopleOutput,
	type PeopleProfile,
	PluginName,
	type TeamworkDocument,
	type TeamworkOutput,
	type TeamworkTeam,
} from './types';

type ParsedMarkdownLink = {
	name: string;
	url: string;
	accountId: string | null;
};

/**
 * Parses a markdown-style link and extracts the display name and accountId.
 *
 * Handles links in format: [Name](/people/accountId)
 * Returns the name with accountId appended in parentheses if found.
 *
 * @param text - The text that may contain a markdown link
 * @returns Object with name, url, accountId, or null if not a valid link
 */
export const parseMarkdownLink = (text: string): ParsedMarkdownLink | null => {
	const match = text.match(/\[([^\]]+)\]\(([^)]+)\)/);
	if (!match) {
		return null;
	}

	const [, name, url] = match;
	const accountIdMatch = url.match(/\/people\/([^/]+)/);
	const accountId = accountIdMatch ? accountIdMatch[1] : null;

	return { name, url, accountId };
};

/**
 * Formats a markdown link as display text with accountId.
 *
 * @param text - The text that may contain a markdown link
 * @returns Formatted string like "Name (accountId)" or original text if not a link
 */
export const formatMarkdownLinkAsText = (text: string): string => {
	const parsed = parseMarkdownLink(text);
	if (!parsed) {
		return text;
	}
	return parsed.accountId ? `${parsed.name} (${parsed.accountId})` : parsed.name;
};

export const parseTeamworkOutput = (output: string): TeamworkOutput | null => {
	const lines = output.split('\n');
	const result: TeamworkOutput = { sections: [] };
	let currentTeam: Partial<TeamworkTeam> | null = null;
	let currentDocument: Partial<TeamworkDocument> | null = null;
	let inMembersSection = false;
	let headerLines: string[] = [];
	let currentSection: {
		sectionHeader?: string;
		teams?: TeamworkTeam[];
		documents?: TeamworkDocument[];
	} = {};

	lines.forEach((line) => {
		const trimmedLine = line.trim();

		// Capture main header
		if (trimmedLine.startsWith('Here are') && !result.header && !currentTeam && !currentDocument) {
			headerLines.push(trimmedLine);
			return;
		}

		// Section headers (like "Here are the Confluence documents:")
		if (
			trimmedLine.startsWith('Here are the') &&
			trimmedLine.endsWith(':') &&
			!currentTeam &&
			!currentDocument
		) {
			// Save previous section if exists
			if (currentSection.teams || currentSection.documents) {
				result.sections.push(currentSection);
			}
			currentSection = { sectionHeader: trimmedLine.slice(0, -1) }; // Remove trailing colon
			return;
		}

		// Start of a new team
		if (trimmedLine.startsWith('- type: Team')) {
			// Save previous item
			if (currentTeam) {
				if (!currentSection.teams) {
					currentSection.teams = [];
				}
				currentSection.teams.push(currentTeam as TeamworkTeam);
			}
			if (currentDocument) {
				if (!currentSection.documents) {
					currentSection.documents = [];
				}
				currentSection.documents.push(currentDocument as TeamworkDocument);
			}
			currentTeam = { type: 'Team', members: [] };
			currentDocument = null;
			inMembersSection = false;
			return;
		}

		// Start of a new document (Confluence Page, Confluence Blog Post, etc.)
		if (trimmedLine.startsWith('- type: Confluence') || trimmedLine.startsWith('- type: Jira')) {
			// Save previous item
			if (currentTeam) {
				if (!currentSection.teams) {
					currentSection.teams = [];
				}
				currentSection.teams.push(currentTeam as TeamworkTeam);
			}
			if (currentDocument) {
				if (!currentSection.documents) {
					currentSection.documents = [];
				}
				currentSection.documents.push(currentDocument as TeamworkDocument);
			}
			currentDocument = { type: trimmedLine.substring(8).trim() };
			currentTeam = null;
			inMembersSection = false;
			return;
		}

		// Parse fields
		if (currentTeam) {
			// Team parsing
			if (inMembersSection && trimmedLine.startsWith('- [')) {
				currentTeam.members!.push(trimmedLine.substring(2).trim());
			} else if (trimmedLine.startsWith('- ')) {
				const colonIndex = trimmedLine.indexOf(':');
				if (colonIndex !== -1) {
					const fieldName = trimmedLine.substring(2, colonIndex).toLowerCase();
					const fieldValue = trimmedLine.substring(colonIndex + 1).trim();

					switch (fieldName) {
						case 'ari':
							currentTeam.ari = fieldValue;
							break;
						case 'name':
							currentTeam.name = fieldValue;
							break;
						case 'description':
							currentTeam.description = fieldValue;
							break;
						case 'creator':
							currentTeam.creator = fieldValue;
							break;
						case 'member count':
							currentTeam.memberCount = parseInt(fieldValue, 10);
							break;
						case 'teamstate':
							currentTeam.teamState = fieldValue;
							inMembersSection = false;
							break;
						case 'members':
							inMembersSection = true;
							currentTeam.members = [];
							break;
					}
				}
			}
		} else if (currentDocument && trimmedLine.startsWith('- ')) {
			// Document parsing
			const colonIndex = trimmedLine.indexOf(':');
			if (colonIndex !== -1) {
				const fieldName = trimmedLine.substring(2, colonIndex).toLowerCase();
				const fieldValue = trimmedLine.substring(colonIndex + 1).trim();

				switch (fieldName) {
					case 'ari':
						currentDocument.ari = fieldValue;
						break;
					case 'title':
						currentDocument.title = fieldValue;
						break;
					case 'url':
						currentDocument.url = fieldValue;
						break;
					case 'author':
						currentDocument.author = fieldValue;
						break;
					case 'updated':
						currentDocument.updated = fieldValue;
						break;
				}
			}
		}
	});

	// Save last item
	if (currentTeam) {
		if (!currentSection.teams) {
			currentSection.teams = [];
		}
		currentSection.teams.push(currentTeam as TeamworkTeam);
	}
	if (currentDocument) {
		if (!currentSection.documents) {
			currentSection.documents = [];
		}
		currentSection.documents.push(currentDocument as TeamworkDocument);
	}

	// Save last section
	if (currentSection.teams || currentSection.documents) {
		result.sections.push(currentSection);
	}

	if (headerLines.length > 0) {
		result.header = headerLines.join(' ');
	}

	return result.sections.length > 0 ? result : null;
};

export const parsePeopleOutput = (output: string): PeopleOutput | null => {
	const lines = output.split('\n');
	const result: PeopleOutput = { people: [] };
	let currentPerson: { header?: string; accountId?: string; profile: PeopleProfile } | null = null;
	let inProfileSection = false;

	const savePerson = () => {
		if (currentPerson && currentPerson.profile.name) {
			result.people.push(currentPerson);
		}
	};

	for (const line of lines) {
		const trimmedLine = line.trim();

		// Parse header line to extract account ID - this starts a new person
		if (trimmedLine.startsWith('Retrieved the following information for user')) {
			// Save previous person if exists
			savePerson();

			// Start new person
			currentPerson = { profile: {} };
			currentPerson.header = trimmedLine;
			const accountIdMatch = trimmedLine.match(/account\/user id \(([^)]+)\)/);
			if (accountIdMatch) {
				currentPerson.accountId = accountIdMatch[1];
			}
			inProfileSection = false;
			continue;
		}

		// Check for profile section start
		if (trimmedLine === '# Profile:') {
			inProfileSection = true;
			continue;
		}

		// Parse profile fields (format: - **Key**: Value)
		if (inProfileSection && currentPerson && trimmedLine.startsWith('- **')) {
			const fieldMatch = trimmedLine.match(/^- \*\*([^*]+)\*\*:\s*(.*)$/);
			if (fieldMatch) {
				const [, fieldName, fieldValue] = fieldMatch;
				const profile = currentPerson.profile;

				switch (fieldName.toLowerCase()) {
					case 'name':
						profile.name = fieldValue;
						break;
					case 'picture':
						profile.picture = fieldValue;
						break;
					case 'email':
						profile.email = fieldValue;
						break;
					case 'timezone':
						profile.timezone = fieldValue;
						break;
					case 'locale':
						profile.locale = fieldValue;
						break;
					case 'nickname':
						profile.nickname = fieldValue;
						break;
					case 'job title':
						profile.jobTitle = fieldValue;
						break;
					case 'location':
						profile.location = fieldValue;
						break;
					case 'department':
						profile.department = fieldValue;
						break;
					case 'organization':
						profile.organization = fieldValue;
						break;
				}
			}
		}
	}

	// Save the last person
	savePerson();

	return result.people.length > 0 ? result : null;
};

export const parsePageSearchOutput = (output: string): PageSearchOutput | null => {
	const lines = output.split('\n');
	const result: PageSearchOutput = { pages: [] };
	let currentPage: Partial<PageSearchPage> | null = null;
	let headerCaptured = false;

	const savePage = () => {
		if (currentPage && currentPage.title) {
			result.pages.push(currentPage as PageSearchPage);
		}
	};

	for (const line of lines) {
		const trimmedLine = line.trim();

		// Capture header
		if (!headerCaptured && trimmedLine.startsWith('Here are')) {
			result.header = trimmedLine;
			headerCaptured = true;
			continue;
		}

		// Start of a new page entry (title line)
		if (trimmedLine.startsWith('- title:')) {
			// Save previous page if exists
			savePage();
			currentPage = { title: trimmedLine.substring(9).trim() };
			continue;
		}

		// Parse page fields
		if (currentPage) {
			if (trimmedLine.startsWith('url:')) {
				currentPage.url = trimmedLine.substring(4).trim();
			} else if (trimmedLine.startsWith('space:')) {
				currentPage.space = trimmedLine.substring(6).trim();
			} else if (trimmedLine.startsWith('date:')) {
				currentPage.date = trimmedLine.substring(5).trim();
			} else if (trimmedLine.startsWith('author:')) {
				currentPage.author = trimmedLine.substring(7).trim();
			} else if (trimmedLine.startsWith('snippet:')) {
				currentPage.snippet = trimmedLine.substring(8).trim();
			}
		}
	}

	// Save the last page
	savePage();

	return result.pages.length > 0 ? result : null;
};

export const parsePluginOutput = (
	pluginName: string | undefined,
	output: string,
): ParsedPluginOutput | null => {
	switch (pluginName) {
		case PluginName.TEAMWORK: {
			const teamworkData = parseTeamworkOutput(output);
			if (teamworkData) {
				return { type: 'teamwork', data: teamworkData };
			}
			break;
		}
		case PluginName.PEOPLE: {
			const peopleData = parsePeopleOutput(output);
			if (peopleData) {
				return { type: 'people', data: peopleData };
			}
			break;
		}
		case PluginName.PAGE_SEARCH_PLUGIN: {
			const pageSearchData = parsePageSearchOutput(output);
			if (pageSearchData) {
				return { type: 'pageSearch', data: pageSearchData };
			}
			break;
		}
	}
	return null;
};
